// src/pages/property-details/components/PropertyOverview.jsx
import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import HoverTooltip from '../../../components/ui/HoverTooltip';

const PropertyOverview = ({ 
  property, 
  isSaved, 
  onSave, 
  onShare, 
  onContact,
  isMarketActionDisabled = false,
  marketActionTooltip = '',
}) => {
  const formatPrice = (price, unit = '') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price) + (unit ? ` ${unit}` : '');
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const totalQuantity = Number(property?.quantity || 0);
  const availableQuantity = Number(property?.availableQuantity ?? property?.quantity ?? 0);
  const soldQuantity = Number(property?.soldQuantity ?? Math.max((property?.quantity || 0) - availableQuantity, 0));
  const hasQuantityDecreased = availableQuantity < totalQuantity;
  const quantityLabel = hasQuantityDecreased
    ? `Kalan ${property?.unit || 'Miktar'}`
    : `Toplam ${property?.unit || 'Miktar'}`;
  const quantityTooltip = `Toplam ${property?.unit || 'miktar'}: ${formatNumber(totalQuantity)}`;

  const getHarvestFreshness = (harvestDate) => {
    const today = new Date();
    const harvest = new Date(harvestDate);
    const diffTime = Math.abs(today - harvest);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Harvested Today';
    if (diffDays === 1) return 'Harvested Yesterday';
    if (diffDays <= 7) return `Harvested ${diffDays} days ago`;
    return `Harvested ${harvest.toLocaleDateString()}`;
  };

  return (
    <div className="card p-6">
      {/* Header Section */}
      <div className="mb-6 lg:grid lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start lg:gap-6">
        <div className="min-w-0">
          <h1
            className="text-xl md:text-2xl lg:text-[1.9rem] font-semibold leading-[1.15] text-text-primary whitespace-normal break-words"
            title={property?.title}
          >
            {property?.title}
          </h1>

          <div className="mt-2 flex items-center flex-wrap gap-2 mb-2">
            {property?.daysListed <= 3 && (
              <span className="bg-success text-white px-2 py-1 rounded-md text-xs font-medium">
                Fresh
              </span>
            )}
            {property?.organic && (
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-xs font-medium">
                Organic
              </span>
            )}
          </div>

          <p className="text-3xl lg:text-4xl font-bold text-primary mb-3">
            {formatPrice(property?.price, property?.priceUnit)}
          </p>

          <div className="flex items-center space-x-2 text-text-secondary mb-4">
            <Icon name="MapPin" size={16} />
            <span>{property?.address}</span>
          </div>

          {property?.productId && (
            <p className="text-sm text-text-secondary">
              Product ID: {property.productId}
            </p>
          )}
        </div>

        {/* Desktop Actions */}
        <div className="hidden lg:grid lg:grid-cols-1 lg:justify-self-end gap-2 lg:pt-1 lg:min-w-[220px] lg:max-w-[240px] w-full">
          <button
            onClick={onSave}
            className={`inline-flex w-full items-center justify-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border transition-all duration-200 ${
              isSaved
                ? 'bg-error text-white border-error shadow-elevation-1'
                : 'bg-surface text-text-secondary border-border hover:text-error hover:border-error hover:shadow-elevation-1'
            }`}
          >
            <Icon name="Heart" size={16} fill={isSaved ? "currentColor" : "none"} />
            <span>{isSaved ? 'Kaydedildi' : 'Kaydet'}</span>
          </button>

          <button
            onClick={onShare}
            className="inline-flex w-full items-center justify-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border border-border bg-surface text-text-secondary hover:text-text-primary hover:border-primary-300 hover:shadow-elevation-1 transition-all duration-200"
          >
            <Icon name="Share" size={16} />
            <span>Paylaş</span>
          </button>

          <HoverTooltip
            content={isMarketActionDisabled ? marketActionTooltip : undefined}
            className="w-full"
          >
            <button
              onClick={onContact}
              disabled={isMarketActionDisabled}
              aria-disabled={isMarketActionDisabled}
              className={`inline-flex w-full items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all duration-200 ${
                isMarketActionDisabled
                  ? 'border-primary bg-primary text-white opacity-[0.65] cursor-not-allowed'
                  : 'border-primary bg-primary text-white shadow-elevation-1 hover:bg-primary-700 hover:shadow-elevation-2'
              }`}
            >
              <Icon name="MessageCircle" size={16} />
              <span>Satıcıya Ulaş</span>
            </button>
          </HoverTooltip>
        </div>
      </div>

      {/* Agricultural Product Features Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <div className="text-center p-3 bg-background rounded-md cursor-help" title={quantityTooltip}>
          <Icon name="Boxes" size={24} className="text-primary mx-auto mb-2" />
          <p className="text-lg font-semibold text-text-primary">
            {formatNumber(availableQuantity)}
          </p>
          <div className="text-sm text-text-secondary">{quantityLabel}</div>
        </div>

        <div className="text-center p-3 bg-background rounded-md">
          <Icon name="ShoppingBasket" size={24} className="text-primary mx-auto mb-2" />
          <p className="text-lg font-semibold text-text-primary">
            {formatNumber(soldQuantity)}
          </p>
          <p className="text-sm text-text-secondary">Kabul Edilen</p>
        </div>

        <div className="text-center p-3 bg-background rounded-md">
          <Icon name="Calendar" size={24} className="text-primary mx-auto mb-2" />
          <p className="text-lg font-semibold text-text-primary">
            {property?.harvestDate ? new Date(property.harvestDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
          </p>
          <p className="text-sm text-text-secondary">Harvest Date</p>
        </div>
        
        {property?.shelfLife && (
          <div className="text-center p-3 bg-background rounded-md">
            <Icon name="Clock" size={24} className="text-primary mx-auto mb-2" />
            <p className="text-lg font-semibold text-text-primary">{property.shelfLife.split(' ')[0]}</p>
            <p className="text-sm text-text-secondary">Days Fresh</p>
          </div>
        )}
        
        {property?.organic && (
          <div className="text-center p-3 bg-background rounded-md">
            <Icon name="Leaf" size={24} className="text-primary mx-auto mb-2" />
            <p className="text-lg font-semibold text-text-primary">Yes</p>
            <p className="text-sm text-text-secondary">Organic</p>
          </div>
        )}
        
        <div className="text-center p-3 bg-background rounded-md">
          <Icon name="Timer" size={24} className="text-primary mx-auto mb-2" />
          <p className="text-lg font-semibold text-text-primary">{property?.daysListed}</p>
          <p className="text-sm text-text-secondary">Days Listed</p>
        </div>
      </div>

      {/* Product Type & Quick Info */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center space-x-2">
          <Icon name="Package2" size={16} className="text-text-secondary" />
          <span className="text-text-secondary">Category:</span>
          <span className="font-medium text-text-primary capitalize">{property?.category}</span>
        </div>
        
        {property?.variety && (
          <div className="flex items-center space-x-2">
            <Icon name="Wheat" size={16} className="text-text-secondary" />
            <span className="text-text-secondary">Variety:</span>
            <span className="font-medium text-text-primary">{property.variety}</span>
          </div>
        )}
        
        {property?.harvestDate && (
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={16} className="text-text-secondary" />
            <span className="text-text-secondary">Freshness:</span>
            <span className="font-medium text-text-primary">{getHarvestFreshness(property.harvestDate)}</span>
          </div>
        )}
      </div>

      {/* Seller Quick Info - Mobile Only */}
      <div className="lg:hidden mt-6 pt-6 border-t border-border">
        <div className="flex items-center space-x-4">
          <Image
            src={property?.seller?.avatar}
            alt={property?.seller?.name}
            className="w-12 h-12 rounded-full object-cover"
          />
          <div className="flex-1">
            <h3 className="font-semibold text-text-primary">{property?.seller?.name}</h3>
            <div className="flex items-center space-x-1">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Icon
                    key={i}
                    name="Star"
                    size={12}
                    className={i < Math.floor(property?.seller?.rating || 0) ? 'text-warning fill-current' : 'text-secondary-300'}
                  />
                ))}
              </div>
              <span className="text-xs text-text-secondary">
                {property?.seller?.rating} ({property?.seller?.reviewsCount} reviews)
              </span>
            </div>
          </div>
          <HoverTooltip
            content={isMarketActionDisabled ? marketActionTooltip : undefined}
            className="inline-flex"
          >
            <button
              onClick={onContact}
              disabled={isMarketActionDisabled}
              aria-disabled={isMarketActionDisabled}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                isMarketActionDisabled
                  ? 'border border-primary bg-primary text-white opacity-[0.65] cursor-not-allowed'
                  : 'bg-primary text-white hover:bg-primary-700'
              }`}
            >
              Contact
            </button>
          </HoverTooltip>
        </div>
      </div>
    </div>
  );
};

export default PropertyOverview;
